// Share a vault & manage members — vault roles, adding people, guardrails.
// Sourced from specs/cli.md §3/§6 and specs/web-app.md (members panel).

import { IconShieldLock, IconTerminal2, IconUsers } from "@tabler/icons-react";
import { CommandLine } from "@/components/command-line";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { DocSection, K, P, Points } from "../doc-ui";

export function MembersDoc() {
  return (
    <>
      <P>
        Sharing is by membership: a vault owner adds an existing account on this server as a
        member with a role. There's no acceptance step — the vault simply appears for them on
        their next sync. (To bring in someone who doesn't have an account yet, see{" "}
        <em>Invite &amp; manage users</em> first.)
      </P>

      <DocSection icon={IconShieldLock} title="Vault roles">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Role</TableHead>
              <TableHead>What it allows</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            <TableRow>
              <TableCell className="font-mono">owner</TableCell>
              <TableCell>
                Everything: edit, manage members and roles, restore history, delete the vault. A
                vault can have several owners.
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="font-mono">editor</TableCell>
              <TableCell>Edit and sync content, restore versions.</TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="font-mono">viewer</TableCell>
              <TableCell>Read-only: browse, read, preview history.</TableCell>
            </TableRow>
          </TableBody>
        </Table>
        <P>
          Vault roles are separate from server roles, with one exception: an account whose server
          role is <K>guest</K> can never hold more than <K>viewer</K> in any vault.
        </P>
      </DocSection>

      <DocSection
        icon={IconUsers}
        title="Where to manage members"
        description="Three equivalent surfaces, all owner-gated for changes."
      >
        <Points>
          <li>
            <strong>Web app</strong> — open the vault and click <K>Members</K> in the header:
            view members, add someone from the user directory, change roles, remove.
          </li>
          <li>
            <strong>Obsidian plugin</strong> — the vault's member panel in the plugin settings
            mirrors the same actions.
          </li>
          <li>
            <strong>CLI</strong> — the commands below.
          </li>
        </Points>
        <CommandLine command="cairn vault members <vault>" comment="list members + roles" />
        <CommandLine command="cairn vault add <vault> <user> --role editor" />
        <CommandLine command="cairn vault role <vault> <user> <role>" comment="change a role" />
        <CommandLine command="cairn vault remove <vault> <user>" />
        <CommandLine command="cairn vault leave <vault>" comment="remove yourself" />
        <P>
          Adding someone as <K>owner</K> is allowed — that's how you make a co-owner.
        </P>
      </DocSection>

      <DocSection
        icon={IconShieldLock}
        title="Guardrails you may run into"
      >
        <Points>
          <li>
            <strong>Guests cap at viewer</strong> — adding or promoting a guest above{" "}
            <K>viewer</K> is refused. Ask an admin to raise their server role first.
          </li>
          <li>
            <strong>Last owner</strong> — the only owner of a vault can't leave or demote
            themselves. Add another owner first.
          </li>
        </Points>
      </DocSection>
    </>
  );
}
